"use client";

import Link from "next/link";
import { Card } from "@/components/ui/card";
import { CommunityImage } from "./CommunityImage";
import { getCommunityProductHref } from "@/lib/community/navigation";
import type { PublicCommunityProduct } from "@/lib/community/types";

interface ProductCardProps {
  product: PublicCommunityProduct;
  shopSlug?: string | null;
  returnTo?: string;
}

export function ProductCard({ product, shopSlug, returnTo }: ProductCardProps) {
  const href = getCommunityProductHref(product.shop_slug ?? shopSlug, product.handle, returnTo);

  const cardContent = (
    <Card variant="default" className="h-full overflow-hidden">
      <div className="aspect-square bg-raised relative">
        <CommunityImage
          src={product.featured_image_url}
          alt={product.title ?? "Product"}
          fill
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
          className="rounded-none"
          containerClassName="absolute inset-0"
        />
        {product.is_trending && (
          <span className="absolute left-2 top-2 z-10 border-2 border-retro-line bg-retro-paper px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-brand-green shadow-[2px_2px_0_#173c17]">
            Trending
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="mb-0.5 line-clamp-2 min-h-[2.75em] text-sm font-medium leading-snug text-ink">
          {product.title ?? "Untitled product"}
        </h3>
        <p className="mb-1.5 truncate text-[11px] text-muted">
          {!product.category_name || product.category_name === "Uncategorized"
            ? "\u00A0"
            : product.category_name}
        </p>
        {product.price_range && (
          <p className="text-xs font-semibold text-brand-green">
            {product.price_range}
          </p>
        )}
      </div>
    </Card>
  );

  if (!href) return cardContent;

  return (
    <Link href={href} className="group block h-full">
      {cardContent}
    </Link>
  );
}
